import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  Buttons,
  FlexContainer,
  Spinner,
  Text,
  Inputs,
  InputFormTypes,
} from '@taxdown/piccolo-web';
import { history } from 'Store';
import { setIsLoadingAction, setIsNotLoadingAction } from 'Store/modules/app/actions';
import useTranslation from 'Hooks/useTranslation';
import FormContainer from 'Components/UI/FormContainer';
import CustomTransition from 'Components/CustomTransition';
import Arrow from 'Components/icons/Arrow';
import DocBox from 'Components/DocBox';
import { routes } from 'Constants/routes';
import { SatProps } from '../../types';
import * as Styled from '../CustomInput/styles';
import model from './model.json';
import { useFetchGetBase64, useFetchSaveEfirmaMutation } from './hooks';

const initialState = {
  certificate: '',
  privateKey: '',
  passphrase: '',
};

const EfirmaForm = ({ setScreen, satEfirmaValues }: SatProps): JSX.Element => {
  const [formValues, setFormValues] = useState(initialState);
  const [{ data: certificateBase64, isLoading }, { data: privateKey64, isLoading: loading }] =
    useFetchGetBase64([satEfirmaValues?.efirma?.certificate, satEfirmaValues?.efirma?.privateKey]);
  const [isValidPasswordFormat, setIsValidPasswordFormat] = useState<boolean>();
  const dispatch = useDispatch();
  const setLoading = (): unknown => dispatch(setIsNotLoadingAction());
  const { mutate } = useFetchSaveEfirmaMutation(setLoading);
  const { t } = useTranslation();

  const handleChange = (value: string, id: string): void => {
    setFormValues(prev => ({
      ...prev,
      [id]: value,
    }));
  };

  const handleChangePassword = (value: string, id: string, isValid: boolean): void => {
    setIsValidPasswordFormat(isValid);
    handleChange(value, id);
  };
  const handleOnSubmit = (e: React.ChangeEvent): void => {
    e.preventDefault();
    mutate(formValues);
    dispatch(setIsLoadingAction());
  };

  useEffect(() => {
    if (satEfirmaValues && satEfirmaValues?.efirma && certificateBase64 && privateKey64) {
      setFormValues({
        certificate: certificateBase64,
        passphrase: satEfirmaValues?.efirma?.passphrase,
        privateKey: privateKey64,
      });
    }
    if (satEfirmaValues?.efirma?.passphrase) {
      handleChangePassword(satEfirmaValues?.efirma?.passphrase, 'passphrase', true);
    }
  }, [satEfirmaValues, certificateBase64, privateKey64]);

  return (
    <CustomTransition start>
      <Styled.BackButton
        onClick={() => {
          history.replace(routes.ACCOUNT);
          setScreen(1);
        }}
      >
        <Arrow />
        {t('common:BackButton')}
      </Styled.BackButton>
      <FlexContainer direction="column" id="rfcContainer" align="center" gap="dimension_16">
        <FlexContainer direction="column" align="flex-start" gap="dimension_24">
          <Text type="headline" color="dark" size="S_L_S" align="start" id="fiscalData">
            {t('common:Efirma.profile.title')}
          </Text>
          <Text type="paragraphs" size="S_M_S" color="dark">
            {t('common:Efirma.profile.subtitle')}
          </Text>
        </FlexContainer>
        {Boolean(satEfirmaValues?.efirma?.certificate) &&
        Boolean(satEfirmaValues?.efirma?.privateKey) &&
        (isLoading || loading) ? (
          <FlexContainer justify="center" align="center">
            <Spinner size="M" justify="center" align="center" />
          </FlexContainer>
        ) : (
          <FormContainer onSubmit={handleOnSubmit}>
            <FlexContainer direction="column" gap="dimension_16">
              {model.map(item => (
                <DocBox
                  key={item.id}
                  testID={item.testID}
                  label={item.label}
                  setValue={handleChange}
                  aceptFiles={item.aceptFiles}
                  id={item.id}
                  value={satEfirmaValues?.efirma[item.id]}
                />
              ))}
              <Inputs.InputForm
                inputForm={{
                  id: 'passphrase',
                  type: InputFormTypes.PASSWORD,
                  label: t('dg:DGEfirma.inputLabel'),
                  placeholder: t('dg:DGEfirma.input.placeholder'),
                  validationType: 'Regex',
                  validations: {
                    required: true,
                    regex: /^.{8,256}$/,
                  },
                }}
                value={formValues.passphrase}
                onChange={(value, isValid) => handleChangePassword(value, 'passphrase', isValid)}
              />
              <Buttons.Classic
                size="l"
                mode="primary"
                disabled={Object.values(formValues).includes('') || !isValidPasswordFormat}
                type="submit"
                testID="btnSubmit"
                label={t('common:SaveEfirma.account')}
              />
            </FlexContainer>
          </FormContainer>
        )}
      </FlexContainer>
    </CustomTransition>
  );
};

export default EfirmaForm;
